import type { ColumnDef, SortingState } from '@tanstack/react-table';
import type { RoleListResponse } from 'api/role/Role.schemas.ts';
import cx from 'clsx';
import { dateToYMDHM } from 'common/date.ts';
import { fromQuerystring, POST, parseFormValues, toQuerystring, truncate } from 'common/helpers.ts';
import { isEqual } from 'es-toolkit';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { ROLE } from 'types/Role.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { IconBadge } from 'ui/component/IconBadge.tsx';
import { IAdd, IFilters, ISearch } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tab } from 'ui/component/Tab.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';
import { UserForm } from '#admin/component/UserForm.tsx';
import { UserListItemMenu } from '#admin/component/UserListItemMenu.tsx';

export const getColumns = (onAction: (action: 'lock' | 'unlock' | 'delete') => void, t: any) => {
  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'id',
      header: t('labels.atlasId'),
      size: 120,
      cell: (info) => {
        const id = info.getValue<string>();
        return (
          <CopyableText className="font-mono" copyText={id}>
            <Link className="anchor anchor-novisit" to={`/user/${id}`}>
              {truncate(id, 8, '')}
            </Link>
          </CopyableText>
        );
      },
    },
    {
      accessorKey: 'logistics_ex_id',
      header: t('labels.providerId'),
      size: 120,
    },
    {
      accessorKey: 'created_at',
      header: t('labels.creationDate'),
      cell: (info) => {
        const value = info.getValue<string>();
        return value ? dateToYMDHM(value) : null;
      },
    },
    {
      accessorKey: 'email',
      header: t('labels.email'),
      minSize: 260,
      meta: { truncate: true },
      cell: (info) => {
        const value = info.getValue<string[]>();
        return (
          <a href={`mailto:${value}`} rel="noreferrer">
            {value}
          </a>
        );
      },
    },
    {
      accessorKey: 'name',
      header: t('labels.name'),
      meta: { truncate: true },
    },
    {
      accessorKey: 'group_name',
      header: t('labels.group'),
      meta: { truncate: true },
    },
    {
      accessorKey: 'group_role',
      header: t('labels.groupRole'),
      meta: { truncate: true },
    },
    {
      accessorKey: 'isActive',
      header: t('labels.status'),
      enableSorting: false,
      size: 100,
      cell: (info) => {
        const value = info.getValue<boolean>();
        return <Tag className={cx('!p-2', value ? 'green' : 'red')}>{value ? t('status.enabled') : t('status.disabled')}</Tag>;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 40,
      meta: { className: '!p-0', hideFallback: true },
      cell: (info) => <UserListItemMenu onAction={onAction} row={info.row} />,
    },
  ];

  return columns;
};

type FormValues = {
  search: string;
  group_role: ROLE | '';
  is_active: boolean | '';
};

export const UserList = () => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRestoreScrollPositionOnBack('users-scroll');
  const [openUserForm, setOpenUserForm] = React.useState<boolean>(false);
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'created_at', desc: true }]);

  const defaultValues: FormValues = { search: '', group_role: '', is_active: '' };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams, { excludeKeys: ['size'] }) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState, reset, getValues, register, watch } = formContext;

  const { data: roles, isLoading: rolesIsLoading } = useSWR<RoleListResponse>(['/role/list', { name$not: 'MEMBER', sort: [['created_at', 'asc']] }], POST);
  const { data: rowsCount, mutate: mutateCount } = useSWR(formState.isValid ? ['/user/count', { ...values, is_group: false }, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/user/list', 50, { ...values, is_group: false }, sorters, parseFormValues), POST, {
    revalidateFirstPage: false,
  });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);

  const toggleFilters = () => setShowFilters(!showFilters);

  const resetFilters = () => {
    reset({ ...defaultValues, group_role: currentTab });
    triggerSubmit();
  };

  // wait next tick or URL will not be updated
  const triggerSubmit = React.useCallback(() => window.setTimeout(() => formRef.current?.requestSubmit(), 50), []);

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const onChangeTab = (nextTab: string | number) => {
    const role = String(nextTab) as ROLE | '';
    formContext.setValue('group_role', role);
    triggerSubmit();
  };

  const handleAddUser = () => {
    setOpenUserForm(true);
  };

  const handleAddUserClose = async () => {
    setOpenUserForm(false);
    await swr.mutate();
    void mutateCount();
  };

  // we need to align to chosen tab
  const currentTab = watch('group_role') || '';
  const columns = React.useMemo(
    () =>
      getColumns((action) => {
        void swr.mutate();
        if (action === 'delete') void mutateCount();
      }, t),
    [swr, mutateCount, t],
  );

  const rolesNames = React.useMemo(() => (roles || []).map((role) => role.name), [roles]);

  const tabs: Record<string, string> = React.useMemo(() => {
    const tabs = { '': t('labels.all') };
    for (const roleName of rolesNames) tabs[roleName] = roleName;
    return tabs;
  }, [rolesNames, t]);

  // we can't rely only on formState.isDirty because it's true when defaultValues provided to useForm are changed, but we may have filters set by default provided in the URL
  const clearFiltersEnabled = formState.isDirty || !isEqual(defaultValues, getValues());

  // we need active filters: if filter is of type array then we count if having least 1 selection, otherwise we count if being not nullish
  const filtersCount = React.useMemo(() => {
    let result = Object.values(values).filter((value) => (Array.isArray(value) ? value.length > 0 : value !== '')).length;
    if (currentTab !== '') result--;
    return result;
  }, [values, currentTab]);

  return (
    <main className="view-container">
      <UserForm isOpen={openUserForm} onClose={handleAddUserClose} onSave={handleAddUserClose} />
      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('navigation.users')}</h4>

        <div>
          <Button className="blue" LeftIcon={IAdd} onClick={handleAddUser}>
            {t('actions.addUser')}
          </Button>
        </div>
      </section>

      <section className="mt-4 mb-6 bg-white p-8 pb-4">
        {rolesIsLoading && <Spinner size={24} />}
        {!rolesIsLoading && <Tab tabs={tabs} defaultTab={currentTab} onChange={onChangeTab} />}

        <FormProvider {...formContext}>
          <form id="search-form" ref={formRef} className="my-6 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <input type="hidden" {...register('group_role')} />

            <div className="flex place-content-between place-items-center">
              <Input
                className="w-[360px]"
                name={'search'}
                placeholder={t('placeholders.searchUsers')}
                required={true}
                autoFocus={true}
                RightIcon={ISearch}
                autoComplete={'off'}
                debouncedAutoSubmitForm={'#search-form'}
                debouncedAutoSubmitTimeout={500}
              />

              <IconBadge icon={<IFilters className="size-[24px] fill-gray-400" />} badge={filtersCount || null} onClick={toggleFilters} />
            </div>

            {/* expanded filters section */}
            <div className={cx('flex flex-col gap-6 bg-blue-sky p-4', !showFilters && 'hidden')}>
              <div className="flex flex-row gap-3">
                <SelectMulti
                  label={'Status'}
                  name={'is_active'}
                  options={
                    new Map([
                      [true, t('filters.enabled')],
                      [false, t('filters.disabled')],
                    ])
                  }
                  selectable={'one'}
                  disableSearch={true}
                  closeOnChangedValue={true}
                  required={false}
                  hasClearAll={true}
                />

                {/* handled by tab changing, we need it otherwise it will be undefined when submitting the form */}
                <SelectMulti className="hidden" label={'Role'} name={'role'} options={rolesNames} selectable={'one'} disableSearch={true} closeOnChangedValue={true} />
              </div>

              <div className="flex flex-row gap-4">
                <Button className="blue-outlined h-max min-w-max max-w-max p-1" type="button" onClick={toggleFilters}>
                  Close
                </Button>
                <Button className="blue-outlined ml-auto min-w-max max-w-max" type="button" onClick={resetFilters} disabled={!clearFiltersEnabled}>
                  Clear filters
                </Button>
                <Button className="blue-outlined min-w-max max-w-max" disabled={showFilters && !formState.isDirty} type="submit">
                  Apply
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>

        <Table
          scrollRef={scrollRef}
          className={cx('max-h-[calc(100vh-385px)]')}
          columns={columns}
          swr={{ ...swr, setSize: setSizeInUrl }}
          pagination={true}
          rowsCount={rowsCount}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={50}
          overscan={50}
        />
      </section>
    </main>
  );
};
